'use client';

import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  RefreshSetupProgressOutput,
  StuckClinicsReport,
} from '@borradh-workspace/api-client/types';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

/** `minDays` omitted = the API's SETUP_STUCK_AFTER_DAYS. */
export const stuckSetupQueryOptions = (minDays?: number) =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.stuckSetup(minDays ?? null),
    queryFn: ({ signal }) =>
      apiClient.get<StuckClinicsReport>(
        `admin-terminal/stuck-setup${minDays === undefined ? '' : `?minDays=${minDays}`}`,
        { signal }
      ),
    staleTime: 60_000,
  });

export const useStuckSetup = (minDays?: number) =>
  useQuery(stuckSetupQueryOptions(minDays));

/** Ask Meta again about one clinic's terms and payment method, now. */
export const useRecheckSetupProgress = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (organizationId: string) =>
      apiClient.post<RefreshSetupProgressOutput>(
        `admin-terminal/organizations/${organizationId}/setup-progress/refresh`,
        {}
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminTerminal.stuckSetupAll(),
      });
      toast.success(
        result.metaChecked
          ? 'Checked again with Meta'
          : 'Checked again (Meta not needed for this step)'
      );
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Could not check this clinic again'),
  });
  return {
    recheck: mutation.mutate,
    isRechecking: mutation.isPending,
    recheckingId: mutation.isPending ? mutation.variables : undefined,
  };
};
